<!-- 当前运营控制台源码维护说明，更新时间：2026-09-04 17:58 CST。本周签到展示获得、预计获得和错失积分。 -->
<template>
  <div class="points-page">
    <section class="points-hero">
      <div>
        <p class="eyebrow">HOTX POINTS</p>
        <h1>积分中心</h1>
        <p class="hero-copy">1 积分 = 1 美元。积分累计后可用于套餐与增值服务消费。</p>
      </div>
      <div class="hero-actions">
        <!-- 2026-09-03 CST：积分中心只保留充值快捷入口，具体流程分别由 TRON/BSC 独立页面承载。 -->
        <el-button size="large" @click="router.push('/trx-recharge')">TRON 充值</el-button>
        <el-button size="large" @click="router.push('/bsc-recharge')">BSC 充值</el-button>
        <el-button
          :class="{ 'points-signin-guide': !status?.todaySigned }"
          size="large"
          type="primary"
          :loading="signing"
          :disabled="status?.todaySigned"
          @click="handleSignIn"
        >
          {{ status?.todaySigned ? '今日已签到' : '签到获积分' }}
        </el-button>
      </div>
    </section>

    <el-alert
      v-if="summary?.spendingFrozen"
      title="积分消费已被冻结，签到与赠送积分仍可到账。"
      type="warning"
      show-icon
      :closable="false"
    />

    <section class="metric-grid">
      <article class="metric-card balance-card">
        <span>当前积分</span>
        <strong>{{ money(summary?.balance) }}</strong>
        <small>可超额至 -{{ money(summary?.overdraftLimit) }}</small>
      </article>
      <article class="metric-card"><span>累计获得</span><strong>{{ money(summary?.totalEarned) }}</strong><small>全部入账积分</small></article>
      <article class="metric-card"><span>累计消费</span><strong>{{ money(summary?.totalSpent) }}</strong><small>套餐与增值服务</small></article>
      <article class="metric-card"><span>连续签到</span><strong>{{ status?.streakDays || summary?.streakDays || 0 }} 天</strong><small>{{ summary?.newcomerRewardPending ? '首次签到另得新人奖励' : '坚持签到，持续积累' }}</small></article>
    </section>

    <section class="signin-panel">
      <div class="section-heading">
        <div><p class="eyebrow">ASIA / SHANGHAI</p><h2>本周签到</h2></div>
        <span>北京时间周一至周日</span>
      </div>
      <div v-if="status?.week?.length" class="week-point-overview" aria-label="本周签到积分概览">
        <span class="earned">已获得 <b>{{ money(weekPointOverview.earned) }}</b></span>
        <span class="estimated">预计获得 <b>{{ money(weekPointOverview.estimated) }}</b></span>
        <span class="missed">已错失 <b>{{ money(weekPointOverview.missed) }}</b></span>
      </div>
      <div class="week-row">
        <div
          v-for="(day, index) in status?.week || []"
          :key="day.date"
          class="day-cell"
          :class="{ signed: day.signed, today: day.today, missed: day.pointStatus === 'MISSED', estimated: day.pointStatus === 'ESTIMATED' }"
        >
          <span>{{ weekNames[index] }}</span>
          <b>{{ day.date.slice(5) }}</b>
          <i>{{ dayProgressLabel(day) }}</i>
          <em v-if="day.pointStatus" class="day-points" :class="day.pointStatus.toLowerCase()">
            {{ signInPointLabel(day.pointStatus) }} {{ day.pointStatus === 'MISSED' ? '' : '+' }}{{ money(day.pointAmount) }}
          </em>
        </div>
      </div>
    </section>

    <section class="source-grid">
      <div class="section-heading full"><div><p class="eyebrow">POINT SOURCES</p><h2>积分来源</h2></div></div>
      <article><span>充值入账</span><b>{{ money(summary?.earnedRecharge) }}</b></article>
      <article><span>签到奖励</span><b>{{ money(summary?.earnedSignIn) }}</b></article>
      <article><span>新人奖励</span><b>{{ money(summary?.earnedNewcomer) }}</b></article>
      <article><span>后台赠送</span><b>{{ money(summary?.earnedAdminGift) }}</b></article>
    </section>

    <section class="data-panel">
      <div class="section-heading">
        <div><p class="eyebrow">IMMUTABLE LEDGER</p><h2>积分流水</h2></div>
        <el-select v-model="ledgerDirection" clearable placeholder="全部方向" style="width: 140px" @change="loadLedger">
          <el-option label="获得" value="CREDIT" />
          <el-option label="消费" value="DEBIT" />
        </el-select>
      </div>
      <el-table :data="ledger" v-loading="ledgerLoading" empty-text="暂无积分流水">
        <el-table-column label="时间" min-width="170">
          <template #default="scope">{{ beijingTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="来源" min-width="150">
          <template #default="scope">{{ sourceLabel(scope.row.sourceType) }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="说明" min-width="220" />
        <el-table-column label="积分" width="130" align="right">
          <template #default="scope">
            <strong :class="scope.row.direction === 'CREDIT' ? 'credit' : 'debit'">{{ scope.row.direction === 'CREDIT' ? '+' : '-' }}{{ money(scope.row.amount) }}</strong>
          </template>
        </el-table-column>
        <el-table-column label="余额" width="120" align="right"><template #default="scope">{{ money(scope.row.balanceAfter) }}</template></el-table-column>
      </el-table>
    </section>

    <section class="data-panel">
      <div class="section-heading"><div><p class="eyebrow">CONSUMPTION ORDERS</p><h2>消费订单</h2></div></div>
      <el-table :data="orders" v-loading="ordersLoading" empty-text="暂无消费订单">
        <el-table-column prop="orderId" label="订单号" min-width="190" />
        <el-table-column prop="productRef" label="项目" min-width="150" />
        <el-table-column prop="payablePoints" label="积分" width="100">
          <template #default="scope">{{ money(scope.row.payablePoints) }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="120">
          <template #default="scope"><el-tag :type="orderTag(scope.row.status)">{{ orderLabel(scope.row.status) }}</el-tag></template>
        </el-table-column>
        <el-table-column prop="failureCode" label="失败码" width="140">
          <template #default="scope">{{ scope.row.failureCode || '-' }}</template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="170">
          <template #default="scope">{{ beijingTime(scope.row.createdAt) }}</template>
        </el-table-column>
      </el-table>
    </section>
    <transition name="reward"><div v-if="rewardVisible" class="reward-toast">+{{ rewardAmount }} 积分已到账</div></transition>
  </div>
</template>

<!-- 当前运营控制台源码维护说明，更新时间：2026-09-04 18:18 CST。游客登录后立即刷新积分周历，保留当前页面并展示获得、预计与错失积分。 -->
<script setup lang="ts">
// 更新时间：2026-09-05 01:42:43 CST。时间统一显示北京时间，来源中文化，并直接展示数据库固化的充值说明。
import { CONSOLE_SESSION_CHANGED_EVENT, tokenStorage } from '../api/client';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { useRoute, useRouter } from 'vue-router';
import { pointApi, pointErrorMessage, type ConsumptionOrder, type PointLedgerItem, type SignInDay, type SignInStatus } from '../api/pointApi';
import { usePointStore } from '../stores/points';
import { displayThreeDecimal } from '../utils/formatters';
import { signInPointLabel, summarizeSignInPoints } from '../utils/signInPoints';

const router = useRouter();
const route = useRoute();
const points = usePointStore();
const summary = computed(() => points.summary);
const status = ref<SignInStatus | null>(null);
const ledger = ref<PointLedgerItem[]>([]);
const orders = ref<ConsumptionOrder[]>([]);
const ledgerDirection = ref('');
const ledgerLoading = ref(false);
let requestedSignInHandled = false;
const ordersLoading = ref(false);
const signing = ref(false);
const rewardVisible = ref(false);
const rewardAmount = ref('0.000');
const weekNames = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
const weekPointOverview = computed(() => summarizeSignInPoints(status.value?.week || []));

function money(value?: number | null) { return displayThreeDecimal(value); }

// 更新时间：2026-09-04 CST。后端时间可能携带 UTC 偏移，页面统一转换为北京时间并隐藏 ISO 技术字符。
function beijingTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}:${part('second')}`;
}

// 更新时间：2026-09-04 CST。账本保留稳定的英文枚举，面向用户的来源列统一展示中文名称。
function sourceLabel(value?: string | null) {
  return ({
    PLAN_PURCHASE: '套餐购买',
    SIGN_IN: '签到奖励',
    RECHARGE: '充值入账',
    ADMIN_GIFT: '后台赠送',
    NEWCOMER_REWARD: '新人奖励'
  } as Record<string, string>)[value || ''] || '其他来源';
}

function dayProgressLabel(day: SignInDay) {
  if (day.signed) return '已签';
  if (day.today) return '今天';
  return day.pointStatus === 'MISSED' ? '未签' : '待签到';
}
function orderLabel(value: string) { return ({ PROCESSING: '处理中', COMPLETED: '已完成', FAILED: '失败', REVERSED: '已冲正' } as Record<string, string>)[value] || value; }
function orderTag(value: string) { return ({ COMPLETED: 'success', PROCESSING: 'warning', FAILED: 'danger', REVERSED: 'info' } as Record<string, string>)[value] || 'info'; }

function showGuestPointsNotice() {
  ElMessage.info({
    message: '登录后即可查看积分、签到记录和消费明细。',
    grouping: true,
    duration: 5000
  });
}

function showPointsLoadNotice(error: unknown, fallback: string) {
  // 更新时间：2026-09-04 12:42 CST。会话失效属于登录状态变化，不再向用户展示为系统加载失败。
  if (!tokenStorage.getAccessToken()) {
    showGuestPointsNotice();
    return;
  }
  ElMessage.error(pointErrorMessage(error, fallback));
}

async function loadLedger() {
  if (!tokenStorage.getAccessToken()) {
    showGuestPointsNotice();
    return;
  }
  ledgerLoading.value = true;
  try {
    ledger.value = (await pointApi.ledger({ page: 1, size: 50, direction: ledgerDirection.value || undefined })).items || [];
  } catch (error) {
    showPointsLoadNotice(error, '积分流水加载失败，请稍后重试。');
  } finally {
    ledgerLoading.value = false;
  }
}

async function loadOrders() {
  if (!tokenStorage.getAccessToken()) {
    showGuestPointsNotice();
    return;
  }
  ordersLoading.value = true;
  try {
    orders.value = (await pointApi.orders({ page: 1, size: 30 })).items || [];
  } catch (error) {
    showPointsLoadNotice(error, '消费订单加载失败，请稍后重试。');
  } finally {
    ordersLoading.value = false;
  }
}

async function handleSignIn() {
  if (signing.value || status.value?.todaySigned) return;
  signing.value = true;
  try {
    const next = await pointApi.signIn();
    rewardAmount.value = money(Number(next.totalReward || 0));
    rewardVisible.value = true;
    window.setTimeout(() => { rewardVisible.value = false; }, 2200);
    ElMessage.success('签到成功，积分已到账');
    await Promise.all([
      points.refresh(),
      pointApi.signInStatus().then((value) => { status.value = value; }),
      loadLedger()
    ]);
  } catch (error) {
    ElMessage.error(pointErrorMessage(error, '签到失败，请稍后重试。'));
    await Promise.all([
      points.refresh().catch(() => undefined),
      pointApi.signInStatus().then((value) => { status.value = value; }).catch(() => undefined)
    ]);
  } finally {
    signing.value = false;
  }
}

// 更新时间：2026-09-04 18:18 CST。统一加载私有积分数据，供首次进入和游客弹窗登录成功后复用。
async function loadAuthenticatedPoints() {
  if (!tokenStorage.getAccessToken()) return;
  try {
    await Promise.all([
      points.refresh(),
      pointApi.signInStatus().then((value) => { status.value = value; }),
      loadLedger(),
      loadOrders()
    ]);
  } catch (error) {
    showPointsLoadNotice(error, '积分中心加载失败，请稍后重试。');
  }
}

// 2026-09-10: 顶部入口只触发一次既有签到 API；后端幂等仍是最终防重保障。
async function handleRequestedSignIn() {
  if (requestedSignInHandled || route.query.action !== 'sign-in' || !tokenStorage.getAccessToken()) return;
  requestedSignInHandled = true;
  try {
    if (!status.value?.todaySigned) await handleSignIn();
  } finally {
    const { action: _action, ...query } = route.query;
    await router.replace({ path: route.path, query });
  }
}

function handleConsoleSessionChanged() {
  if (!tokenStorage.getAccessToken()) {
    status.value = null;
    ledger.value = [];
    orders.value = [];
    return;
  }
  void loadAuthenticatedPoints().then(handleRequestedSignIn);
}

onMounted(async () => {
  window.addEventListener(CONSOLE_SESSION_CHANGED_EVENT, handleConsoleSessionChanged);
  // 更新时间：2026-09-04 18:18 CST。游客可参观；弹窗登录后由会话事件原地补载私有数据，不跳转、不丢页面状态。
  if (!tokenStorage.getAccessToken()) {
    showGuestPointsNotice();
    return;
  }
  await loadAuthenticatedPoints();
  await handleRequestedSignIn();
});

onBeforeUnmount(() => {
  window.removeEventListener(CONSOLE_SESSION_CHANGED_EVENT, handleConsoleSessionChanged);
});
</script>

<style scoped>
.points-signin-guide{position:relative;overflow:hidden;box-shadow:0 8px 22px rgba(64,174,206,.2);animation:points-signin-breathe 4s ease-in-out infinite}.points-signin-guide:after{content:"";position:absolute;inset:-50% auto -50% -35%;width:28%;transform:skewX(-18deg);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:points-signin-sweep 4s ease-in-out infinite}@keyframes points-signin-breathe{0%,100%{transform:translateY(0)}50%{transform:translateY(-2px);box-shadow:0 12px 28px rgba(64,174,206,.32)}}@keyframes points-signin-sweep{0%,58%{left:-35%;opacity:0}66%{opacity:.8}84%,100%{left:116%;opacity:0}}@media(prefers-reduced-motion:reduce){.points-signin-guide,.points-signin-guide:after{animation:none}}
.points-page{display:grid;gap:20px}.points-hero{position:relative;overflow:hidden;display:flex;justify-content:space-between;align-items:flex-end;gap:24px;padding:30px;border-radius:18px;color:#f7f9ff;background:linear-gradient(125deg,#081a35 0%,#113967 62%,#0b6f72 100%);box-shadow:0 18px 48px rgba(13,42,78,.2)}.points-hero:after{content:"";position:absolute;right:-60px;top:-100px;width:260px;height:260px;border:1px solid rgba(255,255,255,.16);border-radius:50%;box-shadow:0 0 0 42px rgba(255,255,255,.035),0 0 0 84px rgba(255,255,255,.025)}.points-hero h1{margin:5px 0 8px;font-size:34px}.hero-copy{margin:0;color:#c8d7e8}.hero-actions{z-index:1;display:flex;gap:10px}.eyebrow{margin:0;color:#2b8491;font-size:11px;font-weight:800;letter-spacing:.18em}.points-hero .eyebrow{color:#79dbd0}.metric-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px}.metric-card{display:flex;min-height:116px;flex-direction:column;justify-content:center;padding:22px;border:1px solid #e4eaf0;border-radius:14px;background:#fff}.metric-card span,.metric-card small{color:#758292}.metric-card strong{margin:7px 0;font-size:26px;color:#172b42}.balance-card{border-color:#9edbd1;background:linear-gradient(145deg,#f4fffb,#e7f6f3)}.signin-panel,.data-panel,.source-grid{padding:22px;border:1px solid #e4eaf0;border-radius:16px;background:#fff}.data-panel{overflow-x:auto}.data-panel :deep(.el-table){min-width:720px}.section-heading{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:18px}.section-heading h2{margin:4px 0 0;color:#172b42;font-size:19px}.section-heading>span{color:#8793a0;font-size:13px}.week-point-overview{display:flex;flex-wrap:wrap;gap:9px;margin:-4px 0 14px}.week-point-overview span{display:inline-flex;align-items:center;gap:7px;padding:7px 11px;border:1px solid;border-radius:999px;font-size:12px;font-weight:700}.week-point-overview b{font-size:14px}.week-point-overview .earned{border-color:#9bd8c3;color:#126b52;background:#effaf5}.week-point-overview .estimated{border-color:#f0ce83;color:#91610d;background:#fff8e8}.week-point-overview .missed{border-color:#efb9b4;color:#ad493f;background:#fff3f1}.week-row{display:grid;grid-template-columns:repeat(7,1fr);gap:10px}.day-cell{display:flex;min-height:118px;flex-direction:column;align-items:center;justify-content:center;border:1px solid #e5ebef;border-radius:12px;color:#778493;background:#f8fafb}.day-cell b{margin:5px 0;color:#263b50}.day-cell i{font-style:normal;font-size:12px}.day-cell.signed{border-color:#8fd2c8;color:#12695f;background:#eaf9f5}.day-cell.missed{border-color:#efc6c1;background:#fff8f7}.day-cell.estimated{border-color:#eed69d;background:#fffdf6}.day-cell.today{box-shadow:inset 0 0 0 2px #2a8a83}.day-points{margin-top:7px;padding:4px 7px;border-radius:999px;font-size:11px;font-style:normal;font-weight:800}.day-points.earned{color:#0d7356;background:#d9f3e8}.day-points.estimated{color:#93600a;background:#fcedc7}.day-points.missed{color:#b34d43;background:#f9dedb}.source-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}.source-grid .full{grid-column:1/-1;margin-bottom:2px}.source-grid article{display:flex;justify-content:space-between;padding:15px;border-radius:10px;background:#f5f8fa;color:#607082}.source-grid b{color:#18364c}.credit{color:#16866f}.debit{color:#cf5f55}.reward-toast{position:fixed;z-index:3000;top:90px;left:50%;transform:translateX(-50%);padding:15px 28px;border-radius:999px;color:#fff;font-weight:800;background:#16866f;box-shadow:0 14px 40px rgba(22,134,111,.35)}.reward-enter-active,.reward-leave-active{transition:.3s}.reward-enter-from,.reward-leave-to{opacity:0;transform:translate(-50%,-16px)}@media(max-width:900px){.metric-grid,.source-grid{grid-template-columns:repeat(2,1fr)}.points-hero{align-items:flex-start;flex-direction:column}.week-row{grid-template-columns:repeat(4,1fr)}}@media(max-width:560px){.metric-grid,.source-grid{grid-template-columns:1fr}.points-hero{padding:22px}.hero-actions{width:100%;flex-direction:column}.week-row{grid-template-columns:repeat(2,1fr)}.week-point-overview{display:grid;grid-template-columns:1fr}.week-point-overview span{justify-content:space-between}.data-panel{padding:16px}.data-panel :deep(.el-table){min-width:680px}}
</style>
